<template>
  <section 
    aria-labelledby="customs-component-navigation"
    role="tabpanel" 
    tabindex="0"
    class="swatches-view swatches-view-wrapper">
    <div id="customs-announcer" class="screen-reader-only" aria-live="polite" aria-atomic="true"> {{ announcerText }}</div>
    <div @click="hideSplashPage()" class="swatches-view-header">
      <slot name="buybutton"></slot>
      <div class="swatches-view-header-wrapper">
        <slot name="navigation"></slot>
      </div>
      <tabs></tabs>
    </div>
    <swatches-wrapper
      @click="hideSplashPage()"
      v-show="!activeLibraryOption && !activeUGCOption && activeGroup === name && activeGroup !== 'yourcolors'"
      v-for="(groupsToRender, name) in getTabsSwatches"
      :key="name"
      :swatchesToRender="groupsToRender.subgroups"
      :groupName="name"
      :activeGroup="activeGroup"
      :class="[name + '-container']"
    ></swatches-wrapper>
    <template v-if="isComponentUGCEnabled || isComponentLibraryEnabled || configuration.vct_settings.pattern_maker.enabled || this.configuration.vct_settings.custom_checkerboard.enabled">
      <ugc-tray-wrapper v-show="activeOption" :init="activeOption"></ugc-tray-wrapper>
    </template>

    <pick-your-own-color-tray v-if="getGroupTabs['yourcolors']" v-show="activeGroup === 'yourcolors'"></pick-your-own-color-tray>

    <palettes></palettes>
    <palette-modal></palette-modal>
    <feature-tab-modal></feature-tab-modal>
  </section>
</template>

<script>
import tabs from '@/components/Swatches/TabsComponent';
import swatchesWrapper from '@/components/Swatches/SwatchesWrapper';
import ugcTrayWrapper from '@/components/UGC/Tray/TrayWrapper';
import palettes from '@/components/Swatches/Palettes';
import paletteModal from '@/components/Swatches/PaletteModal';
import selectSwatch from '@/mixins/Customizer/SelectSwatch';
import featureTabModal from '@/components/Modals/FeatureTabs/FeatureTabModal.vue';
import pickYourOwnColorTray from '@/components/PickYourOwnColorTray';
import { subgroupFeatures } from '@/data/constants';

export default {
  name: "swatchesView",
  components: {
    tabs,
    swatchesWrapper,
    ugcTrayWrapper,
    palettes,
    paletteModal,
    featureTabModal,
    pickYourOwnColorTray,
  },
  data() {
    return {
      activeGroup: "",
      activeUGCOption: false,
      activeLibraryOption: false,
      announcerText: "",
    };
  },
  mixins: [selectSwatch],
  computed: {
    activeOption() {
      return this.activeUGCOption || this.activeLibraryOption;
    },
    availableSwatches() {
      return this.$store.state.utils.availableSwatches;
    },
    pdm() {
      return this.$store.state.utils.pdm;
    },
    isApparelEnabled() {
      return this.$store.getters['utils/isApparelEnabled'];
    },
    activeComponent() {
      const isApparel = this.$store.getters['utils/isApparelEnabled'];
      if (isApparel){
        return this.$store.state.standardNavigation.activeSubComponent !== '' ?
          `${this.$store.state.standardNavigation.activeComponent}/${this.$store.state.standardNavigation.activeSubComponent}` :
          this.$store.state.standardNavigation.activeComponent;
      }
      else {
        return this.$store.state.legacyNavigation.activeComponent;
      }
    },
    activeSubComponent() {
      return this.$store.getters['utils/isApparelEnabled']
        ? this.$store.state.standardNavigation.activeSubComponent
        : false;
    },
    swatchesByComponent() {
      return this.$store.state.swatches.swatchesByComponent;
    },
    swatchesRelation() {
      return this.$store.state.swatches.swatchesRelation;
    },
    getTabsSwatches() {
      return this.$store.state.swatches.tabsSwatches[this.activeComponent];
    },
    getGroupTabs() {
      return this.$store.state.swatches.groupTabs;
    },
    getSubgroups() {
      return this.$store.state.swatches.subgroups;
    },
    defaultRecipe() {
      return this.$store.state.recipe.defaultRecipe;
    },
    exclusiveSwatches() {
      return this.$store.state.swatches.exclusiveSwatches[this.activeComponent];
    },
    initRecipe() {
      return this.$store.state.recipe.initRecipe;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    getPalettes() {
      return this.$store.state.swatches.palettes;
    },
    getPasscodePalettes() {
      return this.$store.state.preloader.passcode.paletteId;
    },
    swatchesInPalettes() {
      return this.$store.state.swatches.swatchesInPalettes;
    },
    disabledSwatchesByFeature() {
      return this.$store.state.swatches.disabledSwatchesByFeature;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    isGdmComponentTransparent() {
      const subComponentName = this.$store.state?.standardNavigation?.activeSubComponent;
      if (!subComponentName) return false;
      const activeComponent = this.$store.state?.standardNavigation?.activeComponent;;
      const gdmIndex = this.$store.state.gdm.data.map(item => item.component3D)
      .indexOf(activeComponent);
      const gdmComponent = this.$store.state?.gdm?.data[gdmIndex].component.find(item => item.id === subComponentName);
      if (!gdmComponent) return false;
      return gdmComponent?.transparent;
    },
    ugcBlockedPalette() {
      return this.$store.state.ugc.ugcBlockedPalettes[this.activeComponent] || null;
    },
    blockedPalettes() {
      return this.$store.getters['swatches/lockedPalettes'].reduce((whole, { id }) => {
        if (id && !whole.includes(id)) {
          whole.push(id);
        }
        return whole;
      }, []);
    },
    /*
     * @computed - uniqueSwatches
     * Array with unique swatch IDs with unique_swatch attribute as true
     */
    uniqueSwatches() {
      const swatchesListByComponent = this.$store.state.swatches.swatchesListByComponent[this.activeComponent];

      return this.$store.getters['swatches/uniqueSwatches'].reduce((whole, { id }) => {
        if (swatchesListByComponent && swatchesListByComponent.includes(id) && !whole.includes(id)) {
          whole.push(id);
        }

        return whole;
      }, []);
    },
    isUGCEnabled() {
      return this.$store.getters["utils/isUGCenabled"];
    },
    isLibraryEnabled() {
      return this.$store.getters["utils/isImageLibraryEnabled"];
    },
    isComponentUGCEnabled() {
      if (this.isUGCEnabled) {
        return this.$store.getters["utils/isComponentUGCenabled"]({componentName: this.activeComponent});
      }
      return false;
    },
    isComponentLibraryEnabled() {
      if (this.isLibraryEnabled) {
        return this.$store.getters["utils/isComponentLibraryEnabled"]({componentName: this.activeComponent});
      }
      return false;
    },
  },
  watch: {
    defaultRecipe(newVal) {
      if (newVal !== undefined) {
        this.$nextTick(() => this.blockRelationSwatches());
      }
    },
    initRecipe(newVal) {
      if (newVal !== undefined && newVal.recipe_id !== this.pdm.default_recipe && (this.$store.state.preloader.showPreloader || this.$store.state.utils.isDesignTransfering)) {
        this.$nextTick(() => this.blockSwatchesByPrecreated(newVal.recipe_id));
      }
    },
  },
  methods: {
    hideSplashPage(){
      if (this.$store.state.utils.isSplashModalOpen) {
        this.$eventBus.$emit("splash-page-background-click");
      }
    },
    /***
     * @function isPaletteOnlyBySwatch
     * @param {swatch_id} String
     * If the palette the property palette_only equal true, the swatch
     * should be shown only in the palette.
     */
    isPaletteOnlyBySwatch(swatchId) {
      for (let palette of this.getPalettes) {
        if (palette.swatches.includes(Number(swatchId)) && palette.palette_only) {
          return true;
        }
      }
      return false;
    },

    /**
     * @function hideSwatchByCollapsingFlag
     * @param {swatchId} Number
     * Hide the swatch if the swatch is included either in swatchesByFeature
     * or exclusiveSwatches.
     * This function is used in palette and normal tab.
     */
    hideSwatchByCollapsingFlag(swatchId, ugcOnly = false) {

      // Hide decostish if the model doesn't have ugc applied
      if (ugcOnly && !this.$recipe.checkIfDecostitchIsAllowed({ugcOnly})) {
        return true;
      }

      // Personalization should block without the component selected
      if (this.disabledSwatchesByFeature['personalization'].includes(swatchId)) {
        return true;
      }
      // TODO: Currently we are not having the own key in ugcFeature for customCheckerboad
      // Once it is present, it will be replaced by the ugcfeature key.
      const ugcFeatures = ['patternMaker', 'ugc', 'customCheckerboard'];

      let shouldHide = false;

      // loops through UGC components
      const componentNames = this.$store.getters['ugc/getComponentsWithUGCApplied'];
      componentNames.forEach(componentName => {
        // skip the active component
        if (componentName === this.activeComponent) return;
        // reads the subgroup of the current component
        const { subgroup } = this.recipe_v2.components[componentName];
        // checks if the swatch under test matches at least one of criteria of swatches disabled by UGC feature
        for (let feature of ugcFeatures) {
          // a list of swatch ids that should be blocked
          const blockedSwatchesByFeature = this.disabledSwatchesByFeature[feature];
          if (
            // if the list is not empty
            blockedSwatchesByFeature
            // and the subgroup of the swatch of the current component matches
            && subgroupFeatures[subgroup] !== feature
            // and the swatch under test is in the list
            && blockedSwatchesByFeature.includes(swatchId)
          ) {
            shouldHide = true;
            break;
          }
        }
      });
      if (!shouldHide) {
        shouldHide = this.exclusiveSwatches && this.exclusiveSwatches.includes(Number(swatchId));
      }

      // When the other component has the same feature
      // the second component applied should block the first component
      // that was applied.
      // Example: Apply patterMaker in vamp, Apply the same checkerboard in quarters
      // Some swatches in vamp will be blocked due to quarters restrictions
      if (!shouldHide) {
        return this.hideSwatchByFeatureWithRecipe(subgroupFeatures, swatchId);
      }

      // This is to show the swatches that are exclusive by component
      // e.g. mte-sk8-2-dx lower_ankle and upper_quarter
      if (shouldHide && this.isComponentExclusive()) {
        shouldHide = false;
      }

      return shouldHide;
    },
    hideSwatchByFeatureWithRecipe(subgroupFeatures, swatchId) {
      let activeCurrentComponent = this.activeComponent;
      let activeSubComponent = null;

      if (this.isApparelEnabled) {
        const dataSplit = this.activeComponent.split('/');
        activeCurrentComponent = dataSplit[0];
        activeSubComponent = dataSplit[1] || null;
      }

      return this.$store.getters['ugc/getUGCEnabledComponents'].some((componentName) => {
        const component = this.recipe_v2.components[componentName];
        const { sub_components: subComponents } = component || {};
        let result = false;

        if (componentName !== activeCurrentComponent && component) {
          const feature = subgroupFeatures[component.subgroup] || null;

          result = feature && this.disabledSwatchesByFeature[feature] &&
            this.disabledSwatchesByFeature[feature].includes(swatchId);
        }

        if (!result && subComponents) {
          result = Object.keys(subComponents).some((subComponent) => {
            const feature = subgroupFeatures[subComponents[subComponent].subgroup] || null;

            return (activeSubComponent !== subComponent ||
              (componentName !== activeCurrentComponent && activeSubComponent === subComponent)) &&
              feature && this.disabledSwatchesByFeature[feature] &&
              this.disabledSwatchesByFeature[feature].includes(swatchId);
          });
        }

        return result;
      });
    },
    /**
     * @function shouldIncludeSwatch
     * @param {Object} swacthObj Swatch info from available swatches
     * Evaluates if the swatch should be included in swatch tray.
     * If there is passcode palettes loaded verifies if the swatch is included there.
     * @returns {Boolean}
     */
    shouldIncludeSwatch(swatchObj) {

      if (!swatchObj) {
        return false;
      }

      // hide transparent swatch for Apparel if graphic component transparent is false
      if (this.isApparel && !this.isGdmComponentTransparent && Number(swatchObj.id) === Number(this.$store.state?.gdm?.config[0]?.global?.transparent_swatch_id)) return false;

      if (this.configuration.enableCollapsingSwatches && this.hideSwatchByCollapsingFlag(Number(swatchObj.id), swatchObj.ugcOnly)) {
        return false;
      }

      if (this.swatchesInPalettes.includes(Number(swatchObj.id)) && this.isPaletteOnlyBySwatch(swatchObj.id)) {
        return false;
      }

      let swatchesPalettes = [];
      if (this.getPasscodePalettes && this.getPasscodePalettes.length) {
        const passcodePalettes = this.getPalettes.filter(palette => this.getPasscodePalettes.includes(palette.palette_id));
        for (let item of passcodePalettes) {
          swatchesPalettes = swatchesPalettes.concat(item.swatches);
        }
      }
      // This is useful when the swatch has palette_only but the palette hasn't been
      // downloaded and the swatch needs to be shown in the normal group.
      if (swatchObj.palette_only === true && !swatchObj.onlyVisibleWithPalette && !this.swatchesInPalettes.includes(Number(swatchObj.id))) {
        return true;
      }

      return !swatchObj.palette_only || (
        swatchObj.palette_only
        && swatchesPalettes.includes(Number(swatchObj.id))
        && swatchObj.tabs_only === true
      );
    },
    filterSwatches() {
      this.$store.commit("swatches/SET_TABS_SWATCHES_OBJECT_BY_COMPONENT", {
        component: this.activeComponent,
        object: this.setSwatchesObject()
      });

      this.$eventBus.$emit("selectedTab");
    },
    setSwatchesObject() {
      const tabsObject = JSON.parse(JSON.stringify(this.getSubgroups));
      let paletteTabs = this.$store.state.swatches.palettesByComponent[this.activeComponent];
      let swatchesByComponent = this.swatchesByComponent[this.activeComponent];
      if (String(this.activeComponent).search('/') !== -1) {
        const componentName = String(this.activeComponent).split('/');
        let paletteTabsGroupSub = this.$store.state.swatches.palettesByComponent[this.activeComponent];
        /** Add tabs comming from the group ans subgroup palettes i.e. graphics and stranger things */
        const paletteTabGroup = this.$store.state.swatches.palettesByComponent?.[componentName[0]];
        if (paletteTabGroup) {
          Object.keys(paletteTabGroup).forEach(group => {
            Object.keys(paletteTabGroup[group]).forEach(subgroup => {
              if(paletteTabGroup[group][subgroup].swatches.length > 0) {
                  paletteTabsGroupSub[group][subgroup] = paletteTabGroup[group][subgroup];
              }
            });
          });
        }
        paletteTabs =  paletteTabsGroupSub;
        swatchesByComponent = this.swatchesByComponent['sub_component'];
      }
      const swatchesObject = JSON.parse(JSON.stringify(this.getGroupTabs));
      const uniqueSwatchesPalettes = [];


      if (!this.$store.getters["utils/isUGCenabled"] && !this.configuration.vct_settings.pattern_maker.enabled && !this.configuration.vct_settings.custom_checkerboard.enabled && swatchesObject['add-your-own']) {
        delete swatchesObject['add-your-own'];
      }

      if (!this.$store.getters["utils/isImageLibraryEnabled"] && swatchesObject['image-gallery']) {
        delete swatchesObject['image-gallery'];
      }

      // If exists uniqueSwatches, assure their palettes are part of the activeComponent so can be used later
      // otherwise, uniqueSwatchesPalettes remains as empty array
      if (this.uniqueSwatches.length) {
        Object.keys(tabsObject).forEach(group => {
          Object.keys(tabsObject[group]).forEach(subgroup => {
            if (tabsObject[group][subgroup].palette_id) {
              const currentPaletteTabsSwatches = paletteTabs[group] && paletteTabs[group][subgroup] && paletteTabs[group][subgroup].swatches;
              const palette = this.getPalettes.find(item => item.palette_id == tabsObject[group][subgroup].palette_id);

              if (currentPaletteTabsSwatches && palette && palette.status) {
                if (currentPaletteTabsSwatches && currentPaletteTabsSwatches.filter(({ id }) => this.uniqueSwatches.includes(id)).length) {
                  uniqueSwatchesPalettes.push(palette.palette_id);
                }
              }
            }
          });
        });
      }

      Object.keys(tabsObject).forEach(group => {
        let total = 0;
        Object.keys(tabsObject[group]).forEach(subgroup => {
          const currentSwatchesByComponent = swatchesByComponent?.[group]?.[subgroup];
          if (tabsObject[group][subgroup].palette_id) {
            let currentPaletteTabsSwatches = paletteTabs?.[group]?.[subgroup]?.swatches;
            if (this.configuration.enableCollapsingSwatches && currentPaletteTabsSwatches && this.exclusiveSwatches) {
              currentPaletteTabsSwatches = currentPaletteTabsSwatches && currentPaletteTabsSwatches.filter(item => !this.hideSwatchByCollapsingFlag(Number(item.id), item.ugcOnly));
            }
            const palette = this.getPalettes.find(item => item.palette_id == tabsObject[group][subgroup].palette_id);

            if (currentPaletteTabsSwatches && palette && palette.status) {
              const ugcLockPalette = this.ugcBlockedPalette && this.ugcBlockedPalette === parseInt(palette.palette_id);
              const lockPalette = !this.blockedPalettes.length || this.blockedPalettes.includes(palette.palette_id);

              // If there is ugcLockPalette, then is the only one to be added
              if (ugcLockPalette) {
                total = total + currentPaletteTabsSwatches.length;
                tabsObject[group][subgroup].swatches = currentPaletteTabsSwatches;
              // If there are uniqueSwatchesPalettes, then filter the unique swatches of them before including them
              } else if (uniqueSwatchesPalettes.includes(palette.palette_id)) {
                const currentSwatches = currentPaletteTabsSwatches.filter(({ id }) => this.uniqueSwatches.includes(id));
                total = total + currentSwatches.length;
                tabsObject[group][subgroup].swatches = currentSwatches;
              // If there are NOT uniqueSwatchesPalettes AND lockPalette then filter the locked palette and include them
              } else if (!uniqueSwatchesPalettes.length && lockPalette) {
                total = total + currentPaletteTabsSwatches.length;
                tabsObject[group][subgroup].swatches = currentPaletteTabsSwatches;
              }
            }
          } else if (this.availableSwatches && currentSwatchesByComponent) {
            tabsObject[group][subgroup].swatches = [];
            currentSwatchesByComponent.forEach(swatchId => {
              const swatchObj = this.availableSwatches.find(swatch => swatch.id === swatchId);

              if (this.shouldIncludeSwatch(swatchObj)) {
                total = total + 1;
                if (tabsObject[group][subgroup]) {
                  tabsObject[group][subgroup].swatches.push(swatchObj);
                }
              }
            });
          }
        });
        if (swatchesObject && swatchesObject[group]) {
          swatchesObject[group].total = total;
          swatchesObject[group].subgroups = tabsObject[group];
        }
      });

      return swatchesObject;
    },
    changeVisibleGroup(groupName) {
      this.activeGroup = groupName;
      this.activeUGCOption = groupName === "add-your-own";
      this.activeLibraryOption = groupName === "image-gallery";
    },
    /** Method to get all the swatches not allowed for each active component **/
    blockRelationSwatches() {
      const promises = [];
      let swatchesNotAllowed = [];
      let componentsNotAllowed = [];
      const swatchesIds = this.getRecipeSwatchIds();

      swatchesIds.forEach(swatchIdSelected => {
        if (!this.swatchesRelation[swatchIdSelected]) {
          promises.push(this.$store.dispatch("swatches/GET_RELATION_SWATCH_BY_SWATCH", swatchIdSelected)
            .catch(err => console.error('blockRelationSwatches error: ', err))
          );
        } else {
          swatchesNotAllowed = swatchesNotAllowed.concat(this.getSwatchesNotAllowed(this.swatchesRelation[swatchIdSelected]));
          componentsNotAllowed = componentsNotAllowed.concat(this.$store.getters["swatches/getRestrictedComponents"]({ object: this.swatchesRelation[swatchIdSelected], activeComponent: this.activeComponent }));
        }
      });

      Promise.all(promises).then(results => {
        results.forEach(element => {
          swatchesNotAllowed = swatchesNotAllowed.concat(this.getSwatchesNotAllowed(element));
          componentsNotAllowed = componentsNotAllowed.concat(this.$store.getters["swatches/getRestrictedComponents"]({
            object: element, activeComponent: this.activeComponent
          }));
        });

        this.setExclusiveComponents(componentsNotAllowed);
        this.setSwatches(swatchesNotAllowed);
        this.$nextTick(this.getSwatchesByComponent);
        this.$eventBus.$emit('block-features-by-swatch-id');
      });
    },
    getRecipeSwatchIds() {
      const { components } = this.recipe_v2;
      const { isRandomizeApplied } = this.$store.state.utils;
      const swatchesIds = [];
      const ugcSubgroupArray = ['checkerboard-creator', 'custom-checkerboard'];
      let activeCurrentComponent = this.activeComponent;
      let activeSubComponent = null;

      if (this.isApparelEnabled) {
        const dataSplit = this.activeComponent.split('/');
        activeCurrentComponent = dataSplit[0];
        activeSubComponent = dataSplit[1] || null;
      }

      Object.keys(components).forEach((component) => {
        const componentData = components[component];
        const { sub_components: subComponents, swatch_id: componentSwatchId } = componentData;

        if ((this.isApparelEnabled ||
          (component === activeCurrentComponent && isRandomizeApplied) ||
          component !== activeCurrentComponent) &&
          componentSwatchId && !swatchesIds.includes(componentSwatchId) &&
          !ugcSubgroupArray.includes(componentData.subgroup)) {
          swatchesIds.push(componentSwatchId);
        }

        if (subComponents) {
          Object.keys(subComponents).forEach((subComponent) => {
            const subComponentData = subComponents[subComponent];
            const { swatch_id: subComponentSwatchId } = subComponentData;

            if ((subComponent !== activeSubComponent ||
              component !== activeCurrentComponent && subComponent === activeSubComponent) &&
              subComponentSwatchId && !swatchesIds.includes(subComponentSwatchId) &&
              !ugcSubgroupArray.includes(subComponentData.subgroup)) {
              swatchesIds.push(subComponentSwatchId);
            }
          });
        }
      });

      return swatchesIds;
    },
    getSwatchesByComponent() {
      if (!this.$store.state.swatches.swatchesByComponent[this.activeComponent] || this.activeSubComponent && !this.$store.state.swatches.swatchesByComponent.sub_component) {
        const tempPayload = { isApparelEnabled: this.isApparelEnabled };
        this.$store
        .dispatch("swatches/GET_AVAILABLE_SWATCHES_BY_COMPONENT", tempPayload)
        .then(() => {
          this.$nextTick(() => this.$eventBus.$emit("loadSwatches"))
        });
      } else if (!this.$store.state.swatches.palettesByComponent[this.activeComponent] || this.configuration.enableCollapsingSwatches) {
       this.$eventBus.$emit("loadSwatches");
      }
    },
    /** Return swatch not allowed, depends if exist block by components or it is global */
    getSwatchesNotAllowed(relationObject) {
      if(!relationObject || !relationObject.swatches_not_allowed || relationObject.swatches_not_allowed.length == 0) {
        return [];
      }
      if(relationObject.restricted_components) {
        if(relationObject.restricted_components.includes(this.activeComponent) && this.isExclusiveSwatchComponent(relationObject)) {
          return relationObject.swatches_not_allowed;
        }
      } else {
        return relationObject.swatches_not_allowed;
      }
    },
    /** Validate if the swatch exist in a exclusive component into the recipe **/
    isExclusiveSwatchComponent(relationObject) {
      for(let component of relationObject.restricted_components) {
        if( this.recipe_v2.components[component].swatch_id === relationObject.swatch_id )
          return true;
      }
      return false;
    },
    setExclusiveComponents(exclusiveComponents) {
      const components = exclusiveComponents.filter(component => component !== "");
      this.$store.commit("swatches/SET_EXCLUSIVE_COMPONENTS", { component: this.activeComponent, components });
    },
    setSwatches(swatchesNotAllowed) {
      let swatches = swatchesNotAllowed.filter((item, pos) => swatchesNotAllowed.indexOf(item) === pos);

      swatches = this.blockDecoStitchDefaultSwatches(swatches);

      this.$store.commit("swatches/SET_EXCLUSIVE_SWATCHES", { component: this.activeComponent, swatches });
      this.$nextTick(() => this.$eventBus.$emit('load-relation-swatches'));
    },
     /**
     * @function blockDecoStitchDefaultSwatches
     * @param {Array} swatches
     * Add default swatch id to exclusive swatches when swatch applied has apply_decostitch flag.
     */
    blockDecoStitchDefaultSwatches(swatches) {
      let newSwatches = swatches;

      if (this.$store.state.geometryUpdates.decostitchApplied) {
        const { componentToHide, parentComponent } = this.pdm.components.decoStitch?.options || {};
        const swatchObj = this.$store.getters['swatches/returnSwatchObj']({
          swatchId : this.recipe_v2.components[parentComponent]?.swatch_id,
        });

        if (swatchObj && swatchObj.apply_decostitch) {
          const defaultSwatchId = this.defaultRecipe.components[componentToHide]?.swatch_id;

          newSwatches = defaultSwatchId ? [...newSwatches, defaultSwatchId] : newSwatches;
        }
      }

      return newSwatches;
    },
  },
  created() {
    this.$eventBus.$on("tabsDataAndPalettesLoaded", this.filterSwatches);
    this.$eventBus.$on("changeVisibleGroup", this.changeVisibleGroup);
    this.$eventBus.$on("blockRelationSwatches", this.blockRelationSwatches);
  },
  beforeDestroy() {
    this.$eventBus.$off("tabsDataAndPalettesLoaded", this.filterSwatches);
    this.$eventBus.$off("changeVisibleGroup", this.changeVisibleGroup);
    this.$eventBus.$off("blockRelationSwatches", this.blockRelationSwatches);
  }
};
</script>
